import { styled } from '@mui/system'
import { colors, fontSizes, BORDER_RADIUSES } from '../../../Components/consts'
import { globalScrollbarStyles } from '../../../Components/Themes/globalStyles'
import { Img } from '../../../Components/Image/Img'
import { Head } from '../../../Components/Typography/Text.styles'
import { Head3 } from '../../../Components/Typography/Text.styles'

export const HeaderContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  marginBottom: theme.spacing(4),
  position: 'relative',
  overflow: 'hidden',
  borderRadius: BORDER_RADIUSES.medium,
  boxShadow: '0 16px 40px rgba(0, 0, 0, 0.18), 0 4px 12px rgba(0, 0, 0, 0.12)',
  transform: 'translateZ(0)', // Hardware acceleration
}))

export const HeaderBackground = styled('div')(({ theme }) => ({
  background: `linear-gradient(135deg, ${colors.blue} 0%, ${colors.darkBlue} 65%, #001440 100%)`,
  padding: theme.spacing(7, 3, 6),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundImage:
      'url("https://images.unsplash.com/photo-1519766304817-4f37bda74b38?ixlib=rb-4.0.3&auto=format&fit=crop&w=1500&q=80")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.12,
    zIndex: 0,
    filter: 'contrast(1.3) saturate(1.4)',
    mixBlendMode: 'soft-light',
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      'linear-gradient(to bottom, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.2) 100%), radial-gradient(circle at top right, rgba(255,255,255,0.15) 0%, rgba(0,0,0,0) 60%), radial-gradient(circle at bottom left, rgba(255,203,6,0.08) 0%, rgba(0,0,0,0) 60%)',
    zIndex: 1,
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(6, 2, 5),
  },
}))

export const HeaderContent = styled('div')(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
}))

export const ProfilePictureContainer = styled('div')(({ theme }) => ({
  width: 90,
  height: 90,
  borderRadius: '50%',
  overflow: 'hidden',
  border: '3px solid rgba(255, 255, 255, 0.2)',
  marginBottom: theme.spacing(2.5),
  position: 'relative',
  boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(5px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    borderRadius: '50%',
    border: '1px solid rgba(255, 255, 255, 0.4)',
  },
  [theme.breakpoints.down('sm')]: {
    width: 70,
    height: 70,
    marginBottom: theme.spacing(2),
  },
}))

export const ProfilePicture = styled(Img)({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
})

export const ProfileInitials = styled('div')({
  color: colors.white,
  fontSize: fontSizes.large,
  fontWeight: 600,
  opacity: 0.9,
})

export const AuthorName = styled('div')(({ theme }) => ({
  color: colors.yellow,
  fontSize: fontSizes.medium,
  fontWeight: 700,
  marginBottom: theme.spacing(0.5),
  textShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
  letterSpacing: '0.5px',
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-4px',
    left: '25%',
    width: '50%',
    height: '2px',
    background: `linear-gradient(to right, rgba(255,203,6,0.2) 0%, ${colors.yellow} 50%, rgba(255,203,6,0.2) 100%)`,
    borderRadius: '1px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.small,
  },
}))

export const HeaderTitle = styled(Head)(({ theme }) => ({
  color: colors.white,
  fontSize: fontSizes.xlarge,
  fontWeight: 800,
  textShadow: '0 2px 12px rgba(0, 0, 0, 0.4)',
  letterSpacing: '0.5px',
  lineHeight: 1.2,
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.medium,
  },
}))

export const HeaderSubtitle = styled('div')(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.95)',
  fontSize: fontSizes.small,
  fontWeight: 500,
  marginTop: theme.spacing(2),
  textTransform: 'uppercase',
  letterSpacing: 2,
  position: 'relative',
  padding: theme.spacing(0, 2),
  '&::before, &::after': {
    content: '""',
    position: 'absolute',
    top: '50%',
    width: '40px',
    height: '1px',
    background:
      'linear-gradient(to right, rgba(255,255,255,0) 0%, rgba(255,255,255,0.7) 50%, rgba(255,255,255,0) 100%)',
  },
  '&::before': {
    left: '-30px',
  },
  '&::after': {
    right: '-30px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.xSmall,
    marginTop: theme.spacing(1.5),
    '&::before, &::after': {
      width: '25px',
    },
    '&::before': {
      left: '-15px',
    },
    '&::after': {
      right: '-15px',
    },
  },
}))

export const AccentText = styled('div')({
  color: colors.yellow,
  textShadow: '0 2px 12px rgba(0, 0, 0, 0.5)',
  position: 'relative',
  display: 'inline-block',
})

export const HeaderDecoration = styled('div')({
  position: 'absolute',
  width: 250,
  height: 250,
  borderRadius: '50%',
  background:
    'radial-gradient(circle, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0) 70%)',
  top: -120,
  right: 70,
  zIndex: 1,
})

export const HeaderDecorationLeft = styled('div')({
  position: 'absolute',
  width: 200,
  height: 200,
  borderRadius: '50%',
  background: 'radial-gradient(circle, rgba(255,203,6,0.08) 0%, rgba(255,203,6,0) 70%)',
  bottom: '-80px',
  left: '-60px',
  zIndex: 1,
})

export const HeaderBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  right: theme.spacing(2),
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(8px)',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: '20px',
  fontSize: fontSizes.xSmall,
  color: colors.white,
  fontWeight: 600,
  letterSpacing: '1px',
  textTransform: 'uppercase',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
    padding: theme.spacing(0.5, 1),
    top: theme.spacing(1.5),
    right: theme.spacing(1.5),
  },
}))

export const DraftBoardContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  margin: theme.spacing(2, 0),
  width: '100%',
}))

export const PlayerBoardCon = styled('div')<{ fullHeight: boolean }>(
  ({ fullHeight }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxHeight: fullHeight === true ? '100%' : 450,
    ...globalScrollbarStyles,
  })
)

export const PlayerListContainer = styled('div')<{ fullHeight: boolean }>(
  ({ theme, fullHeight }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    maxHeight: fullHeight === true ? '100%' : 450,
    overflowY: 'scroll',
    overflowX: 'hidden',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
  })
)

export const PlayerCardContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  borderRadius: BORDER_RADIUSES.small,
  border: `1px solid ${colors.offWhite}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
}))

export const PlayerCardHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  backgroundColor: colors.white,
  cursor: 'pointer',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
  },
}))

export const CollegeImageContainer = styled('div')(({ theme }) => ({
  width: 50,
  height: 50,
  borderRadius: '50%',
  overflow: 'hidden',
  marginRight: theme.spacing(2),
  border: `1px solid ${colors.offWhite}`,
  flexShrink: 0,
  backgroundColor: colors.offWhite,
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

export const CollegeImage = styled(Img)(({ theme }) => ({
  '& img': {
    margin: theme.spacing(2),
    width: 40,
    height: 40,
    objectFit: 'fill',
  },
}))

export const PlayerInitials = styled('div')(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: fontSizes.small,
  fontWeight: 600,
  color: colors.gray.gray70,
  backgroundColor: colors.offWhite,
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.xSmall,
  },
}))

export const PlayerInfo = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
}))

export const PlayerNameContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(0.5),
  },
}))

export const PlayerName = styled(Head3)(({ theme }) => ({
  fontSize: fontSizes.medium,
  fontWeight: 600,
  margin: 0,
  color: colors.text,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.small,
  },
}))

export const PositionBadge = styled('div')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .posCode': {
    fontSize: fontSizes.xSmall,
    fontWeight: 600,
    color: colors.text,
    [theme.breakpoints.down('sm')]: {
      fontSize: fontSizes.xSmall,
    },
  },
  backgroundColor: colors.offWhite,
  borderRadius: 4,
  padding: theme.spacing(0.25, 0.75),
  minWidth: 28,
  textAlign: 'center',
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.xSmall,
    padding: theme.spacing(0.2, 0.5),
    minWidth: 24,
  },
}))

export const PlayerDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(0.5),
  '& .playerDetail': {
    display: 'flex',
    alignItems: 'center',
    '& p': {
      fontSize: fontSizes.small,
      color: colors.gray.gray70,
      marginRight: theme.spacing(1.5),
      [theme.breakpoints.down('sm')]: {
        fontSize: fontSizes.xSmall,
      },
    },
  },
}))

export const CollegeLogoContainer = styled('div')(({ theme }) => ({
  display: 'none',
  [theme.breakpoints.down('sm')]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: theme.spacing(0.5),
    width: 16,
    height: 16,
  },
}))

export const CollegeLogo = styled(Img)({
  '& img': {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
})

export const PlayerRanking = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(1),
  },
}))

export const RankBadge = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: '60px',
  padding: theme.spacing(1),
  borderRadius: BORDER_RADIUSES.xSmall,
  backgroundColor: colors.offWhite,
  [theme.breakpoints.down('sm')]: {
    minWidth: 50,
    padding: theme.spacing(0.5),
  },
}))

export const RankValue = styled('div')(({ theme }) => ({
  fontSize: fontSizes.medium,
  fontWeight: 600,
  color: colors.text,
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.small,
  },
}))

export const ChangeValue = styled('div')<{ change: number }>(({ theme, change }) => ({
  fontSize: fontSizes.medium,
  fontWeight: 600,
  color: change > 0 ? colors.green : change < 0 ? colors.red : colors.text,
  [theme.breakpoints.down('sm')]: {
    fontSize: fontSizes.small,
  },
}))

export const RankLabel = styled('div')(({ theme }) => ({
  fontSize: fontSizes.xSmall,
  color: colors.gray.gray70,
  textTransform: 'uppercase',
  [theme.breakpoints.down('sm')]: {
    fontSize: '8px',
  },
}))

export const PlayerAnalysis = styled('div')<{ isExpanded: boolean }>(
  ({ theme, isExpanded }) => ({
    padding: isExpanded ? theme.spacing(3) : 0,
    backgroundColor: colors.offWhite,
    maxHeight: isExpanded ? 500 : 0,
    overflow: 'hidden',
    transition: 'all 0.2s ease-in-out',
    [theme.breakpoints.down('sm')]: {
      padding: isExpanded ? theme.spacing(2) : 0,
    },
    '& .title': {
      fontSize: fontSizes.small,
      fontWeight: 600,
      color: colors.text,
      margin: theme.spacing(0, 0, 1, 0),
      [theme.breakpoints.down('sm')]: {
        fontSize: fontSizes.xSmall,
      },
    },
    '& .analysis': {
      fontSize: fontSizes.small,
      color: colors.text,
      margin: 0,
    },
  })
)

export const ExpandButton = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  color: colors.black,
  '& svg': {
    transition: 'transform 0.3s ease',
  },
}))

export const LoadingContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  width: '100%',
}))
